import { useEffect, useMemo, useRef, useState } from "react";

import isEmpty from "lodash/isEmpty";
import Image from "next/image";
import { FormattedMessage, useIntl } from "react-intl";

import { Button } from "@/components/atoms/Button";
import { Text } from "@/components/atoms/Text";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/molecules/Carousel";
import { Breakpoints } from "@/foundations/breakpoints/breakpoints";
import Cube20 from "@/icons/__generated__/Cube/Cube20";
import PhotoCover from "@/icons/__generated__/PhotoCover/PhotoCover20";
import PlayCircle20 from "@/icons/__generated__/PlayCircle/PlayCircle20";
import { useLazyLoadingCarouselImages } from "@/lib/hooks/use-lazy-loading-carousel-images";
import {
  getMediaTypeLabels,
  MEDIA_TYPES,
} from "~/components/gallery/media-type.const";
import type { PhotoGalleryProps } from "~/components/gallery/photo-gallery.types";
import { ListingSaveButton } from "~/components/save/listing-save-button";
import { ListingShareButton } from "~/components/share/listing-share-button";
import { useListingData } from "~/context/ListingPageDataContext";
import { IVideoEmbedData } from "~/types/api-types";
import { cloudinaryImageLoader } from "~/utils/image-loaders";

import AwardsWinnerBadge from "./AwardsWinnerBadge";

export const PhotoGallery = ({
  category,
  emailShare,
  fbShare,
  images,
  isFav,
  isMDScreen,
  listingAwards,
  listingCity,
  listingCountry,
  listingId,
  metro,
  twitterShare,
  useType,
  analyticData,
  onImgSelected,
  toggleFavorite,
}: PhotoGalleryProps) => {
  const intl = useIntl();
  const label = getMediaTypeLabels(intl);
  const [api, setApi] = useState<CarouselApi>();
  const carouselRef = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(1);
  const { loadedImages } = useLazyLoadingCarouselImages(
    carouselRef,
    images.map((image) => image?.id || ""),
    api,
    true,
  );
  const { data } = useListingData();
  const { videos } = data;

  const hasVideos =
    videos &&
    videos.some(
      (video: IVideoEmbedData) => video?.embed_type === MEDIA_TYPES.VIDEO,
    );

  const has3DTour =
    videos &&
    videos.some(
      (tour: IVideoEmbedData) => tour.embed_type === MEDIA_TYPES.VIRTUAL_TOUR,
    );

  useEffect(() => {
    if (!api) {
      return;
    }

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  const galleryImages = useMemo(
    () =>
      images.length < 5
        ? [...images.slice(1), ...Array(5 - images.length).fill({})]
        : images.slice(1, 5),
    [images],
  );

  const opts = useMemo(
    () => ({
      loop: true,
      breakpoints: {
        [`(min-width: ${Breakpoints.SM}px)`]: { active: false },
      },
    }),
    [],
  );

  return (
    <div className="tw-w-full tw-relative tw-pt-[80%] sm:tw-pt-[50%]">
      <div className="tw-absolute tw-top-0 tw-left-0 tw-size-full">
        <div className="tw-flex tw-gap-12 tw-h-full tw-relative">
          <Carousel
            className="tw-h-full [&>div>div]:tw-h-full sm:tw-rounded-4"
            opts={opts}
            setApi={setApi}
            ref={carouselRef}
          >
            <CarouselContent>
              <CarouselItem key="first-image" className="tw-group tw-relative">
                <Button
                  variant="text-only"
                  iconOnly
                  onClick={onImgSelected}
                  aria-label={
                    `View full gallery starting with ${images[0].alt}` ||
                    "cover image"
                  }
                  className="tw-size-full [&>span]:tw-relative [&>span]:tw-size-full"
                >
                  <Image
                    src={images[0].id}
                    alt={images[0].alt || ""}
                    fill
                    sizes={`(max-width: ${Breakpoints.SM}px) 100vw, 40vw`}
                    loader={cloudinaryImageLoader}
                    priority
                    fetchPriority="high"
                    loading="eager"
                    className="tw-bg-gray-200 group-hover:tw-brightness-90 tw-object-cover tw-object-center"
                  />
                </Button>
              </CarouselItem>
              {images.slice(1).map((image, idx) => (
                <CarouselItem key={idx + 1} className="tw-group tw-relative">
                  {loadedImages.has(image.id || "") && (
                    <Button
                      variant="text-only"
                      iconOnly
                      onClick={onImgSelected}
                      aria-label={
                        `View full gallery starting with ${image.alt}` ||
                        `gallery image ${idx + 2}`
                      }
                      className="tw-size-full [&>span]:tw-relative [&>span]:tw-size-full"
                    >
                      <Image
                        src={image.id}
                        alt={image.alt || ""}
                        fill
                        sizes={`(max-width: ${Breakpoints.SM}px) 100vw, 40vw`}
                        loader={cloudinaryImageLoader}
                        className="tw-bg-gray-200 group-hover:tw-brightness-90 tw-object-cover tw-object-center"
                      />
                    </Button>
                  )}
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="tw-absolute tw-top-8 tw-right-8 tw-flex sm:tw-hidden tw-gap-8 [&>button]:tw-rounded-circular [&>button]:tw-shadow-xl">
              <ListingShareButton
                origin={"share listing details"}
                useType={useType}
                metro={metro}
                category={category}
                fbShare={fbShare}
                twitterShare={twitterShare}
                emailShare={emailShare}
                isMDScreen={false}
                appearance="white"
                analyticData={analyticData}
              />
              <ListingSaveButton
                isFav={isFav}
                listingId={listingId}
                toggleFavorite={toggleFavorite}
                isMDScreen={false}
                appearance="white"
              />
            </div>
            {!isEmpty(listingAwards) && (
              <div className="tw-absolute tw-top-16 tw-left-16">
                <AwardsWinnerBadge
                  listingAwards={listingAwards}
                  listingCity={listingCity}
                  listingCountry={listingCountry}
                  listingUseType={useType}
                  isMDScreen={isMDScreen}
                />
              </div>
            )}
            <div className="tw-absolute tw-bottom-20 tw-right-20 tw-py-4 tw-px-8 tw-bg-gray-900/70 tw-backdrop-blur-sm tw-rounded-full tw-flex sm:tw-hidden hover:tw-bg-gray-900/70 hover:tw-backdrop-blur-sm">
              <Text typeface="body-1-m-long" className="tw-text-base-white">
                {`${current}/${images.length}`}
              </Text>
            </div>
          </Carousel>
          <div className="tw-w-full tw-hidden sm:tw-grid tw-grid-cols-2 tw-grid-rows-2 tw-gap-12">
            {galleryImages.map((image, i) => (
              <Button
                variant="text-only"
                iconOnly
                onClick={onImgSelected}
                aria-label={`View full gallery starting with ${image.alt || `gallery image ${i + 2}`}`}
                className=" tw-group tw-size-full [&>span]:tw-relative [&>span]:tw-size-full"
                key={`photo-${image.id}-${i}`}
              >
                {image.id ? (
                  <Image
                    src={image.id}
                    alt={image.alt || ""}
                    fill
                    loader={cloudinaryImageLoader}
                    sizes="20vw"
                    className="tw-rounded-4 tw-bg-gray-200 group-hover:tw-brightness-90 tw-object-cover tw-object-center"
                  />
                ) : (
                  <div className="tw-size-full tw-bg-gray-200 tw-rounded-4 group-hover:tw-brightness-90" />
                )}
              </Button>
            ))}
          </div>
          {(has3DTour || hasVideos || isMDScreen) && (
            <div className="tw-absolute tw-bottom-12 tw-left-12 tw-p-6 tw-rounded-full md:tw-bg-brand-white/30 md:tw-backdrop-blur-[2px] tw-gap-8 tw-flex tw-items-center">
              {has3DTour && (
                <Button
                  shape="pill"
                  href="#type=virtual_tour"
                  className="max-md:tw-bg-gray-900/70 max-md:tw-backdrop-blur-[2px] max-md:tw-text-base-white max-md:tw-border-transparent hover:max-md:tw-bg-gray-900/70 hover:max-md:tw-backdrop-blur-sm"
                >
                  <div className="tw-flex tw-gap-4 md:tw-gap-8 tw-items-center">
                    <Cube20 />
                    <Text typeface="caption-2-m" className="md:tw-body-2-m">
                      {label[MEDIA_TYPES.VIRTUAL_TOUR]}
                    </Text>
                  </div>
                </Button>
              )}
              {hasVideos && (
                <Button
                  shape="pill"
                  href="#type=videos"
                  className="max-md:tw-bg-gray-900/70 max-md:tw-backdrop-blur-[2px] max-md:tw-text-base-white max-md:tw-border-transparent hover:max-md:tw-bg-gray-900/70 hover:max-md:tw-backdrop-blur-sm"
                >
                  <div className="tw-flex tw-gap-4 md:tw-gap-8 tw-items-center">
                    <PlayCircle20 />
                    <Text typeface="caption-2-m" className="md:tw-body-2-m">
                      {label[MEDIA_TYPES.VIDEO]}
                    </Text>
                  </div>
                </Button>
              )}
              <Button
                shape="pill"
                href="#type=media"
                className="tw-hidden md:tw-flex"
              >
                <div className="tw-flex tw-gap-4 md:tw-gap-8 tw-items-center">
                  <PhotoCover />
                  <Text typeface="caption-2-m" className="md:tw-body-2-m">
                    <FormattedMessage
                      id="listing.listing_multi_image.view_all_btn"
                      defaultMessage="View all"
                    />
                  </Text>
                </div>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
